import { Firestore, doc, setDoc, getDoc, runTransaction, writeBatch } from '@angular/fire/firestore';
import { Database, ref as dbRef, set, get } from '@angular/fire/database';
import { captureException } from '@sentry/angular';
import { createDefaultSettings, SettingsSchema } from '../models/user-data.codec';
import { Progress } from './migration-types';
import { MachineModel } from '../models/machine.model';
import { ServiceUpdateModel } from '../models/service.model';

/**
 * Phase 3 Migration: Firestore Migration
 *
 * One-time migration from RTDB and legacy Firestore paths to new standardized structure.
 * This is the newest migration and runs last (after image and service update migrations).
 *
 * Flow:
 * 1. Check if already migrated (users/{uid}/migration/firestoreMigrated)
 * 2. Migrate settings from RTDB to Firestore (users/{uid}/settings field)
 * 3. Migrate machines from RTDB to Firestore (users/{uid}/machines/)
 * 4. Migrate service updates from RTDB to Firestore (users/{uid}/serviceUpdates/)
 * 5. Migrate profile from legacy Firestore path to new path (users/{uid}/profile field)
 * 6. Set migration flags in both Firestore and RTDB
 *
 * Graceful Fallback for New Users:
 * - All RTDB reads are wrapped in try/catch blocks
 * - If RTDB read fails (PERMISSION_DENIED or path doesn't exist), falls back to defaults
 * - This allows migration to complete successfully for new users with no legacy data
 * - Common in E2E tests and new user signups where RTDB was never populated
 *
 * Note: After this migration, RTDB write access is disabled via security rules.
 */
export async function* migrateToFirestore(
  uid: string,
  db: Database,
  firestore: Firestore,
): AsyncGenerator<Progress, void, unknown> {
  // Check if already migrated
  try {
    const migrationDoc = await getDoc(doc(firestore, `users/${uid}`));
    if (
      migrationDoc.exists() &&
      migrationDoc.data()?.['migration']?.['firestoreMigrated']
    ) {
      yield { type: 'done' };
      return;
    }
  } catch {
    console.log('Migration status check failed, proceeding with migration');
  }

  const totalSteps = 4; // settings, machines, service updates, profile

  yield {
    type: 'progress',
    current: 0,
    total: totalSteps,
    message: 'Starting Firestore migration...',
  };

  try {
    // Step 1: Migrate settings from RTDB (or create default if they don't exist)
    yield {
      type: 'progress',
      current: 1,
      total: totalSteps,
      message: 'Migrating settings...',
    };

    let settingsData = null;
    try {
      const settingsSnapshot = await get(dbRef(db, `/userProfile/${uid}/settings`));
      settingsData = settingsSnapshot.val();
    } catch (error) {
      // RTDB read failed (likely PERMISSION_DENIED or path doesn't exist)
      // This is okay for new users with no legacy data
      console.log('RTDB settings read failed (likely new user):', error);
      settingsData = null;
    }
    const settings = settingsData ? SettingsSchema.parse(settingsData) : createDefaultSettings();

    // Settings are already plain objects from Zod schema
    await setDoc(
      doc(firestore, `users/${uid}`),
      { settings },
      { merge: true },
    );

    // Step 2: Migrate machines from RTDB
    yield {
      type: 'progress',
      current: 2,
      total: totalSteps,
      message: 'Migrating machines...',
    };

    let machinesData = null;
    try {
      const machinesSnapshot = await get(dbRef(db, `/userProfile/${uid}/machines`));
      machinesData = machinesSnapshot.val();
    } catch (error) {
      // RTDB read failed (likely PERMISSION_DENIED or path doesn't exist)
      console.log('RTDB machines read failed (likely new user):', error);
      machinesData = null;
    }
    const machines = machinesData
      ? Object.entries(machinesData).map(([id, machineData]) =>
          new MachineModel({ ...(machineData as object), id })
        )
      : [];

    // Use batch write for machines (max 500 per batch)
    const batchSize = 500;
    for (let i = 0; i < machines.length; i += batchSize) {
      const batch = writeBatch(firestore);
      const chunk = machines.slice(i, i + batchSize);

      for (const machine of chunk) {
        const machineData = JSON.parse(JSON.stringify(machine));
        const machineRef = doc(firestore, `users/${uid}/machines/${machine.id}`);
        batch.set(machineRef, machineData);
      }

      await batch.commit();
    }

    // Step 3: Migrate service updates from RTDB (only those with valid machineId)
    yield {
      type: 'progress',
      current: 3,
      total: totalSteps,
      message: 'Migrating service updates...',
    };

    let servicesData = null;
    try {
      const servicesSnapshot = await get(dbRef(db, `/userProfile/${uid}/serviceupdates`));
      servicesData = servicesSnapshot.val();
    } catch (error) {
      // RTDB read failed (likely PERMISSION_DENIED or path doesn't exist)
      console.log('RTDB service updates read failed (likely new user):', error);
      servicesData = null;
    }
    const services = servicesData
      ? Object.entries(servicesData).map(([id, serviceData]) =>
          ({ ...(serviceData as object), id }) as ServiceUpdateModel
        )
      : [];

    // Filter to only properly linked service updates
    const validServices = services.filter(
      (service) => service.machineId && service.id,
    );
    const droppedCount = services.length - validServices.length;

    if (droppedCount > 0) {
      console.warn(
        `Migration: Dropped ${droppedCount} orphaned service updates (missing machineId or id)`,
      );
      captureException(
        new Error('Orphaned service updates found during migration'),
        {
          extra: {
            userId: uid,
            droppedCount,
            totalServices: services.length,
          },
        },
      );
    }

    // Use batch write for service updates (max 500 per batch)
    for (let i = 0; i < validServices.length; i += batchSize) {
      const batch = writeBatch(firestore);
      const chunk = validServices.slice(i, i + batchSize);

      for (const service of chunk) {
        const serviceData = JSON.parse(JSON.stringify(service));
        const serviceRef = doc(
          firestore,
          `users/${uid}/serviceUpdates/${service.id}`,
        );
        batch.set(serviceRef, serviceData);
      }

      await batch.commit();
    }

    // Step 4: Migrate profile from legacy /userProfile/{uid} to /users/{uid}/profile
    yield {
      type: 'progress',
      current: 4,
      total: totalSteps,
      message: 'Migrating profile...',
    };

    // Check if profile exists in legacy location
    const legacyProfileDoc = await getDoc(doc(firestore, `/userProfile/${uid}`));
    const profileData = legacyProfileDoc.exists() ? legacyProfileDoc.data() : null;

    // Use transaction to atomically update user doc with profile and migration status
    await runTransaction(firestore, async (transaction) => {
      const userDocRef = doc(firestore, `users/${uid}`);

      const migrationData = {
        migration: {
          firestoreMigrated: true,
          migratedAt: new Date(),
          version: '2.0.0',
          steps: {
            settings: true,
            machines: true,
            serviceUpdates: true,
            profile: !!profileData,
          },
          stats: {
            machineCount: machines.length,
            serviceUpdateCount: validServices.length,
            droppedServiceUpdates: droppedCount,
          },
        },
        ...(profileData ? { profile: profileData } : {}),
      };

      transaction.set(userDocRef, migrationData, { merge: true });
    });

    // Set RTDB flag for security rules (best-effort for legacy users)
    try {
      await set(dbRef(db, `/userProfile/${uid}/firestoreMigrated`), true);
    } catch (error) {
      // RTDB write failed (likely PERMISSION_DENIED for new users with no legacy data)
      // This is okay - new users don't need this flag since they never had RTDB data
      console.log('RTDB migration flag write failed (likely new user):', error);
    }
  } catch (error) {
    captureException(error, {
      extra: {
        userId: uid,
        step: 'firestore-migration',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      },
    });
    throw error;
  }

  yield { type: 'done' };
}
